import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  fetchRegistrationsReportAction,
  setLoading,
} from "../slices/registrationsReportSlice";
import { FetchRegistrationsReportParams } from "../api/fetchRegistrationsReport/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

export interface RegistrationsReportFilters {
  searchTerm?: string;
  statuses?: string[];
  sessionDateFrom?: string;
  sessionDateTo?: string;
}

function buildFilterSortParams(
  filters: RegistrationsReportFilters,
  sorting: SortingState,
): Omit<FetchRegistrationsReportParams, "page" | "pageSize"> {
  const params: Omit<FetchRegistrationsReportParams, "page" | "pageSize"> = {};
  if (filters.searchTerm) params.searchTerm = filters.searchTerm;
  if (filters.statuses?.length) params.statuses = filters.statuses;
  if (filters.sessionDateFrom) params.sessionDateFrom = filters.sessionDateFrom;
  if (filters.sessionDateTo) params.sessionDateTo = filters.sessionDateTo;
  if (sorting.length > 0) {
    params.sortBy = sorting[0].id;
    params.sortOrder = sorting[0].desc ? "desc" : "asc";
  }
  return params;
}

export function useRegistrationsReport() {
  const dispatch = useAppDispatch();

  const { items, totalCount, loading, error } = useSelector(
    (state: RootState) => state.registrationsReport,
  );

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<RegistrationsReportFilters>({});

  useEffect(() => {
    dispatch(
      fetchRegistrationsReportAction({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        ...buildFilterSortParams(filters, sorting),
      }),
    );
  }, [dispatch, pagination, sorting, filters]);

  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      dispatch(setLoading(true));
      setPagination(newPagination);
    },
    [dispatch],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      dispatch(setLoading(true));
      setSorting((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (
      key: keyof RegistrationsReportFilters,
      value: string | string[] | undefined,
    ) => {
      dispatch(setLoading(true));
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  return {
    items,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    exportParams: buildFilterSortParams(filters, sorting),
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
